import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeSlash } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lesson-card-manager/artifacts/zd0x6jcg_c982a405-739a-4717-a6a0-d9774dc13353_20251118_231830_0000.png";

export default function RegisterPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('student');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (password.length < 6) {
            setError('Wachtwoord moet minimaal 6 tekens zijn');
            return;
        }
        
        setLoading(true);
        const result = await register(email, password, name, role);
        setLoading(false);

        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="login-split">
            {/* Hero section - Dark with logo */}
            <div 
                className="login-hero relative flex items-center justify-center"
                style={{
                    backgroundColor: '#1A202C'
                }}
            >
                <div className="relative z-10 text-center p-8">
                    <img 
                        src={LOGO_URL} 
                        alt="Rijschool AutoPilot" 
                        className="mx-auto mb-6 w-64 h-64 object-contain"
                    />
                    <p className="text-lg text-gray-400 max-w-md italic">
                        "Drive like a pilot"
                    </p>
                </div>
            </div>

            {/* Register form - Dark theme */}
            <div className="flex items-center justify-center p-8 bg-[#1A202C]">
                <div className="w-full max-w-md">
                    <div className="bg-[#2D3748] p-8 rounded-lg border border-[#4A5568]">
                        <div className="mb-8">
                            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Registreren
                            </h2>
                            <p className="text-gray-400 mt-2">
                                Maak een account aan om te beginnen.
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm" data-testid="register-error">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-gray-300">Naam</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder="Je volledige naam"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 focus:ring-2 focus:ring-[#D4A537]"
                                    data-testid="register-name-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-300">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="jouw@email.nl"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 focus:ring-2 focus:ring-[#D4A537]"
                                    data-testid="register-email-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-gray-300">Wachtwoord</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Minimaal 6 tekens"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 focus:ring-2 focus:ring-[#D4A537] pr-10"
                                        data-testid="register-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                                    >
                                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="role" className="text-gray-300">Ik ben een</Label>
                                <Select value={role} onValueChange={setRole}>
                                    <SelectTrigger className="bg-[#1A202C] border-[#4A5568] text-white" data-testid="register-role-select">
                                        <SelectValue placeholder="Selecteer je rol" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#2D3748] border-[#4A5568]">
                                        <SelectItem value="student" className="text-white hover:bg-[#3D4A5C]">Leerling</SelectItem>
                                        <SelectItem value="instructor" className="text-white hover:bg-[#3D4A5C]">Instructeur</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                                disabled={loading}
                                data-testid="register-submit-button"
                            >
                                {loading ? 'Bezig met registreren...' : 'Registreren'}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm text-gray-400">
                            Al een account?{' '}
                            <Link to="/login" className="text-[#D4A537] font-medium hover:underline" data-testid="login-link">
                                Inloggen
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
